import { useState, useEffect } from 'react';
import { getValidToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || '';

// Score → color: red (0) → yellow (50) → green (100)
function scoreColor(score) {
  if (score >= 70) return '#22c55e';
  if (score >= 50) return '#eab308';
  if (score >= 35) return '#f97316';
  return '#ef4444';
}

function MomentumBadge({ momentum }) {
  if (momentum > 0) return <span className="text-xs font-semibold" style={{ color: '#22c55e' }}>▲ Bullish</span>;
  if (momentum < 0) return <span className="text-xs font-semibold" style={{ color: '#ef4444' }}>▼ Bearish</span>;
  return <span className="text-xs font-semibold text-gray-500">→ Flat</span>;
}

// Ranked list of the best cheap ($0.01–$0.70) 0-1 DTE SPX contracts.
// Backend computes IV + greeks (Black-Scholes) and a composite score from
// gamma/delta per dollar, liquidity, reachability, theta burn and the
// "fallen angel" revival signal. Refreshes every 5 s.
export default function BestContractsWidget({ onContractSelect }) {
  const [data, setData] = useState(null);
  const [connStatus, setConnStatus] = useState('connecting');
  const [selectedSymbol, setSelectedSymbol] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function pollOnce() {
      try {
        const token = await getValidToken();
        const res = await fetch(`${API_URL}/api/spx-best`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = await res.json();
        if (mounted) { setData(d); setConnStatus('live'); }
      } catch {
        if (mounted) setConnStatus('reconnecting');
      }
    }
    pollOnce();
    const timer = setInterval(pollOnce, 5000);
    return () => { mounted = false; clearInterval(timer); };
  }, []);

  const handleRowClick = (c) => {
    setSelectedSymbol(c.symbol);
    if (onContractSelect) {
      onContractSelect({ symbol: c.symbol, price: c.buy, type: c.type, strike: c.strike });
    }
  };

  const contracts = data?.contracts || [];
  const emEntries = data ? Object.entries(data.expected_moves || {}) : [];

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">

      {/* Header */}
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          SPX Best Cheap Contracts
        </span>
        <span className="text-xs text-gray-500">$0.01 – $0.70 · 0-1 DTE</span>

        {data && <MomentumBadge momentum={data.momentum} />}

        {data?.news_active && (
          <span className="text-xs px-2 py-0.5 rounded"
            style={{ backgroundColor: '#eab30822', color: '#eab308' }}
            title={data.news_headline}>
            ⚡ News
          </span>
        )}

        <div className="flex-1" />

        {data?.spot > 0 && (
          <span className="text-xs font-mono text-gray-300">SPX ≈ {data.spot.toFixed(2)}</span>
        )}
        <span className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: connStatus === 'live' ? '#22c55e' : '#eab308' }} />
          {connStatus === 'live' ? 'Live · 5s' : connStatus}
        </span>
      </div>

      {/* Expected move per expiry */}
      {emEntries.length > 0 && (
        <div className="flex items-center gap-4 mb-3 text-xs text-gray-500">
          {emEntries.map(([exp, em]) => (
            <span key={exp}>
              {exp} <span className="text-gray-400">EM ±{em.toFixed(0)} pts</span>
            </span>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
              <th className="text-left  py-2 pr-2 font-medium">#</th>
              <th className="text-left  py-2 pr-2 font-medium">Score</th>
              <th className="text-left  py-2 pr-2 font-medium">Type</th>
              <th className="text-right py-2 pr-2 font-medium">Strike</th>
              <th className="text-right py-2 pr-2 font-medium">DTE</th>
              <th className="text-right py-2 pr-2 font-medium">Buy</th>
              <th className="text-right py-2 pr-2 font-medium">TP</th>
              <th className="text-right py-2 pr-2 font-medium">SL</th>
              <th className="text-right py-2 pr-2 font-medium">Δ</th>
              <th className="text-right py-2 pr-2 font-medium">IV</th>
              <th className="text-right py-2 pr-2 font-medium">Sprd</th>
              <th className="text-right py-2 pr-2 font-medium">Vol</th>
              <th className="text-right py-2 pr-2 font-medium">OI</th>
              <th className="text-right py-2 pr-2 font-medium">DayHi</th>
              <th className="text-right py-2       font-medium" title="Day-high ÷ current price — fallen-angel signal">Rev×</th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((c, i) => {
              const isCall = c.type === 'C';
              const typeColor = isCall ? '#22c55e' : '#ef4444';
              const selected = c.symbol === selectedSymbol;
              return (
                <tr key={c.symbol}
                  onClick={() => handleRowClick(c)}
                  className="border-b cursor-pointer"
                  style={{
                    borderColor: '#1f2937',
                    backgroundColor: selected ? '#1e3a5f44' : 'transparent',
                  }}>
                  <td className="py-1.5 pr-2 text-gray-500">{i + 1}</td>
                  <td className="py-1.5 pr-2">
                    <span className="font-bold font-mono" style={{ color: scoreColor(c.score) }}>
                      {c.score.toFixed(1)}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2">
                    <span className="font-semibold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: typeColor + '22', color: typeColor }}>
                      {isCall ? 'CALL' : 'PUT'}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2 text-right font-mono text-gray-200">{c.strike.toFixed(0)}</td>
                  <td className="py-1.5 pr-2 text-right text-gray-400">{c.dte}</td>
                  <td className="py-1.5 pr-2 text-right font-mono font-bold text-gray-100">${c.buy.toFixed(2)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono" style={{ color: '#22c55e' }}>${c.tp.toFixed(2)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono" style={{ color: '#ef4444' }}>${c.sl.toFixed(2)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-gray-300">{c.delta.toFixed(3)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-gray-400">{(c.iv * 100).toFixed(0)}%</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-gray-400">{c.spread_pct.toFixed(0)}%</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-gray-300">{c.volume.toLocaleString()}</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-gray-400">{c.oi.toLocaleString()}</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-gray-400">${c.day_high.toFixed(2)}</td>
                  <td className="py-1.5       text-right font-mono"
                    style={{ color: c.revival >= 3 ? '#eab308' : '#6b7280' }}>
                    {c.revival.toFixed(1)}
                  </td>
                </tr>
              );
            })}
            {contracts.length === 0 && (
              <tr>
                <td colSpan={15} className="py-6 text-center text-gray-600">
                  {connStatus === 'connecting'
                    ? 'Loading…'
                    : 'No contracts pass the filters right now (price $0.01–$0.70, bid ≥ $0.05, spread ≤ 40%, volume ≥ 100, within 2× expected move)'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-2 text-xs text-gray-600">
        Ranked by gamma/delta per dollar, liquidity, reachability, theta burn and revival.
        TP = delta-gamma repricing at ½ expected move · SL = 1-hour theta decay (max −50%).
        Not a prediction — most far-OTM contracts expire worthless.
      </div>
    </div>
  );
}
